import { createBrowserRouter, Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useAuth, resolveAuthenticatedRedirect } from '@/shared/lib/auth';
import { ProtectedLayout, AppShell } from '@/app/shell';
import { FullPageLoader } from '@/shared/components/full-page-loader';
import { NotFound } from '@/shared/components/not-found';
import { OverviewPage } from '@/features/overview';
import { BannersPage } from '@/features/banners';
import { FabButtonsPage } from '@/features/fab-buttons';
import { LoginPage } from '@/features/auth';
import { TenantLayout, SelectStorePage } from '@/features/tenant';
import { DashboardPage } from '@/features/dashboard';
import {
  LeadsPage,
  AuditPage,
  LeadDetailPage,
  LegacyMultiRegistrationEditRedirect,
} from '@/features/leads';
import { OperatorPage } from '@/features/operator';
import { StoreUsersPage } from '@/features/store-users';
import {
  ManualCampaignsPage,
  AutomatedCampaignFormPage,
  CampaignResultsPage,
  UploadCampaignsPage,
  CampaignsIndexRedirect,
  OwnerCampaignsGuard,
} from '@/features/campaigns';
import { SegmentationsPage, SegmentationBuilderPage } from '@/features/segmentacoes';
import { ProductsPage } from '@/features/products';
import { InfluencerListPage } from '@/features/influencer';
import { ClientsPage, ClientFormPage, ClientHubPage, PublicClientRegisterPage } from '@/features/clients';
import { PublicInviteRouteLayout, PublicInvitePage } from '@/features/public-invite';
import { ParticipantPage } from '@/features/participant-area';
import { ModulesPage } from '@/features/catalog';
import { CampaignAgendaClientPage } from '@/features/campaign-agenda';
import { DispatchRunDetailPage } from '@/features/dispatch-log';
import { ObservabilityPage } from '@/features/observability';
import { PlatformLayout, PlatformPage } from '@/features/platform';
import {
  EmailCampaignDetailPage,
  EmailCampaignsPage,
  EmailCampaignWizardPage,
  EmailMarketingIndexRedirect,
  EmailMarketingLayout,
  EmailSettingsPage,
  EmailTemplateFormPage,
  EmailTemplatesLegacyRedirect,
  EmailTemplatesPage,
} from '@/features/email-marketing';
import {
  SmsCampaignDetailPage,
  SmsCampaignsPage,
  SmsCampaignWizardPage,
  SmsMarketingIndexRedirect,
  SmsMarketingLayout,
  SmsSettingsPage,
} from '@/features/sms-marketing';
import { PushCampaignsPage, PushMarketingIndexRedirect, PushMarketingLayout, PushSettingsPage } from '@/features/push-marketing';
import { OwnerPage } from '@/features/owner';
import { PassbookToClientHubRedirect } from '@/features/permissions';
import { MasterGuard } from '@/shared/components/master-guard';
import { StoreSettingsPage, TiersPage, TierOverridePage } from '@/features/settings';
import { VersoCartaoPage } from '@/features/verso-cartao';
import { MetabaseConfigPage } from '@/features/metabase-config';
import {
  LoyaltyCampaignsPage,
  LoyaltyCampaignFormPage,
  LoyaltySettlementPage,
} from '@/features/habibs-loyalty';
import {
  MgmAwareInvitePage,
  MgmCampaignFormPage,
  MgmInviteByCodePage,
  MgmOverviewPage,
} from '@/features/member-get-member';
import {
  NpsHomePage,
  SurveysPage,
  SurveyDetailPage,
  IntegrationsPage,
  IntegrationsCreatePage,
  IntegrationDetailPage,
  NpsUsersPage,
  NpsProfilePage,
  NpsLayout,
  PublicSurveyPage,
  PublicSurveyConfirmPage,
  PublicSurveyRouteLayout,
} from '@/features/nps';
import { MOCK_PUBLIC_SURVEY_ID } from '@/mocks/nps';
import {
  AccountLayout,
  AccountProfilePage,
  AccountPasswordPage,
  AccountQrCodePage,
} from '@/features/account';

function LegacyPublicSurveyRedirect() {
  const { surveyId } = useParams<{ surveyId: string }>();
  if (!surveyId) return <Navigate to="/login" replace />;
  return <Navigate to={`/surveys/${surveyId}`} replace />;
}

function LegacyWhatsappRedirect() {
  const { idPassbook } = useParams<{ idPassbook: string }>();
  if (!idPassbook) return <Navigate to="/selecionar-loja" replace />;
  return <Navigate to={`/loja/${idPassbook}/campanhas`} replace />;
}

function LegacyPushRedirect() {
  const { idPassbook, campaignId } = useParams<{ idPassbook: string; campaignId?: string }>();
  const { pathname } = useLocation();
  if (!idPassbook) return <Navigate to="/selecionar-loja" replace />;
  const leaf = pathname.endsWith('/nova')
    ? '/nova'
    : pathname.endsWith('/editar') && campaignId
      ? `/${campaignId}/editar`
      : pathname.endsWith('/resultados') && campaignId
        ? `/${campaignId}/resultados`
        : '';
  return <Navigate to={`/loja/${idPassbook}/push-marketing/campanhas${leaf}`} replace />;
}

function BlockedLeadsRedirect() {
  const { idPassbook } = useParams<{ idPassbook: string }>();
  if (!idPassbook) return <Navigate to="/selecionar-loja" replace />;
  return <Navigate to={`/loja/${idPassbook}/leads/auditoria?tab=bloqueados`} replace />;
}

/** `/permissoes/*` antigo (com `?passbook=`) → hub do cliente, pela rota da loja que resolve o clientId. */
function LegacyPermissionsRedirect() {
  const { section } = useParams<{ section?: string }>();
  const [params] = useSearchParams();
  const idPassbook = params.get('passbook');
  if (!idPassbook) return <Navigate to="/clientes" replace />;
  return <Navigate to={`/loja/${idPassbook}/permissoes/${section ?? 'programa'}`} replace />;
}

function RootRedirect() {
  const { isLoading, isAuthenticated, user, authSource, startPath } = useAuth();
  if (isLoading) return <FullPageLoader />;
  if (!isAuthenticated || !user) return <Navigate to="/login" replace />;
  return <Navigate to={resolveAuthenticatedRedirect(user, authSource, startPath)} replace />;
}

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  // Cadastro público de cliente — sem autenticação nem shell.
  { path: '/cadastro', element: <PublicClientRegisterPage /> },
  // Cadastro público de lead (fixe.club/invite/:idPassbook) — sem autenticação nem shell.
  {
    element: <PublicInviteRouteLayout />,
    children: [
      // Mesmo cadastro, lembrando o link de indicação aberto antes neste navegador (Indique e Ganhe).
      { path: '/invite/:idPassbook', element: <MgmAwareInvitePage /> },
      // Link de indicação que o participante compartilha: /invite/r/<código>.
      { path: '/invite/r/:code', element: <MgmInviteByCodePage /> },
      // Multi-loja/departamento — legacy/fixe-link-2 routes/invite.js `/:id_passbook/:id_office/:id_department`.
      { path: '/invite/:idPassbook/:idOffice/:idDepartment', element: <PublicInvitePage /> },
      // Área do participante (só web): cadastro + "Minha conta" com código no WhatsApp.
      { path: '/clube/:idPassbook', element: <ParticipantPage /> },
    ],
  },
  // Pesquisa pública NPS — sem autenticação nem shell.
  {
    element: <PublicSurveyRouteLayout />,
    children: [
      { path: '/surveys/confirm', element: <PublicSurveyConfirmPage /> },
      ...(import.meta.env.VITE_USE_MOCKS === 'true'
        ? [
            {
              path: '/surveys/preview',
              element: <Navigate to={`/surveys/${MOCK_PUBLIC_SURVEY_ID}`} replace />,
            },
          ]
        : []),
      { path: '/surveys/:surveyId', element: <PublicSurveyPage /> },
    ],
  },
  { path: '/p/:surveyId', element: <LegacyPublicSurveyRedirect /> },
  {
    element: <ProtectedLayout />,
    children: [
      {
        element: <AppShell />,
        children: [
          { path: 'clientes', element: <ClientsPage /> },
          { path: 'clientes/novo', element: <ClientFormPage /> },
          { path: 'clientes/:id', element: <ClientHubPage /> },
          { path: 'modulos', element: <ModulesPage /> },
          // Agenda, log de disparos e custos de IA viraram abas de Observabilidade.
          { path: 'observabilidade', element: <ObservabilityPage /> },
          // Configurações gerais (e-mails e pushes do sistema): só o master; o editor de
          // template é o mesmo do módulo de e-mail, no escopo da plataforma.
          {
            path: 'plataforma',
            element: <PlatformLayout />,
            children: [
              { index: true, element: <PlatformPage /> },
              { path: 'templates/novo', element: <EmailTemplateFormPage /> },
              { path: 'templates/:templateId/editar', element: <EmailTemplateFormPage /> },
            ],
          },
          { path: 'agenda-campanhas', element: <Navigate to="/observabilidade?aba=campanhas" replace /> },
          { path: 'agenda-campanhas/:clientId', element: <CampaignAgendaClientPage /> },
          { path: 'dispatch-runs', element: <Navigate to="/observabilidade?aba=disparos" replace /> },
          { path: 'dispatch-runs/:runId', element: <DispatchRunDetailPage /> },
          { path: 'custos-ia', element: <Navigate to="/observabilidade?aba=ia" replace /> },
          { path: 'custos-ia/usuarios', element: <Navigate to="/observabilidade?aba=ia" replace /> },
          // Usuários moram no hub de cada cliente.
          { path: 'usuarios', element: <Navigate to="/clientes" replace /> },
          {
            element: <MasterGuard />,
            children: [{ path: 'permissoes/:section?', element: <LegacyPermissionsRedirect /> }],
          },
          { path: 'overview', element: <OverviewPage /> },
          { path: 'owner', element: <OwnerPage /> },
          { path: 'owner/lojas', element: <OwnerPage /> },
          {
            path: 'owner/campanhas',
            element: <OwnerCampaignsGuard />,
            children: [
              { index: true, element: <PushCampaignsPage /> },
              { path: 'nova', element: <AutomatedCampaignFormPage /> },
              { path: ':campaignId/editar', element: <AutomatedCampaignFormPage /> },
              { path: ':campaignId/resultados', element: <CampaignResultsPage /> },
              { path: 'configuracao', element: <PushSettingsPage /> },
            ],
          },
          { path: 'nps', element: <NpsLayout />, children: [
            { index: true, element: <NpsHomePage /> },
            { path: 'pesquisas', element: <SurveysPage /> },
            { path: 'pesquisas/:id', element: <SurveyDetailPage /> },
            { path: 'integracoes', element: <IntegrationsPage /> },
            { path: 'integracoes/nova', element: <IntegrationsCreatePage /> },
            { path: 'integracoes/:id', element: <IntegrationDetailPage /> },
            { path: 'usuarios', element: <NpsUsersPage /> },
            { path: 'perfil', element: <NpsProfilePage /> },
          ] },
          {
            path: 'conta',
            element: <AccountLayout />,
            children: [
              { index: true, element: <AccountProfilePage /> },
              { path: 'senha', element: <AccountPasswordPage /> },
              { path: 'qr-code', element: <AccountQrCodePage /> },
            ],
          },
          { path: 'selecionar-loja', element: <SelectStorePage /> },
          // Compat Metabase / dash antigo → detalhe do lead no v2.
          {
            path: 'multi-registration/edit/:leadId',
            element: <LegacyMultiRegistrationEditRedirect />,
          },
          {
            path: 'loja/:idPassbook',
            element: <TenantLayout />,
            children: [
              { index: true, element: <DashboardPage /> },
              { path: 'leads', element: <LeadsPage /> },
              { path: 'leads/auditoria', element: <AuditPage /> },
              { path: 'leads/bloqueados', element: <BlockedLeadsRedirect /> },
              { path: 'leads/:leadId', element: <LeadDetailPage /> },
              { path: 'operador', element: <OperatorPage /> },
              // Usuários da loja (operadores): acesso só por URL, de propósito —
              // sem item de menu, igual ao legado `/validators`. Não adicionar em
              // `navigation.ts` sem combinar antes.
              { path: 'usuarios', element: <StoreUsersPage /> },
              { path: 'campanhas', element: <CampaignsIndexRedirect /> },
              { path: 'campanhas/manual', element: <ManualCampaignsPage /> },
              { path: 'campanhas/whatsapp', element: <LegacyWhatsappRedirect /> },
              { path: 'campanhas/automatizadas/whatsapp', element: <LegacyWhatsappRedirect /> },
              { path: 'campanhas/automatizadas/nova', element: <LegacyPushRedirect /> },
              { path: 'campanhas/automatizadas/:campaignId/editar', element: <LegacyPushRedirect /> },
              { path: 'campanhas/automatizadas/:campaignId/resultados', element: <LegacyPushRedirect /> },
              { path: 'campanhas/automatizadas', element: <LegacyPushRedirect /> },
              { path: 'campanhas/upload', element: <UploadCampaignsPage /> },
              { path: 'campanhas/fidelidade/nova', element: <LoyaltyCampaignFormPage /> },
              {
                path: 'campanhas/fidelidade/:campaignId/editar',
                element: <LoyaltyCampaignFormPage />,
              },
              { path: 'campanhas/fidelidade/liquidacao', element: <LoyaltySettlementPage /> },
              { path: 'campanhas/fidelidade', element: <LoyaltyCampaignsPage /> },
              { path: 'indique-e-ganhe', element: <MgmOverviewPage /> },
              { path: 'indique-e-ganhe/campanha', element: <MgmCampaignFormPage /> },
              { path: 'indique-e-ganhe/campanha/:campaignId', element: <MgmCampaignFormPage /> },
              { path: 'segmentacoes', element: <SegmentationsPage /> },
              { path: 'segmentacoes/nova', element: <SegmentationBuilderPage /> },
              { path: 'segmentacoes/:segmentationId/editar', element: <SegmentationBuilderPage /> },
              {
                path: 'email-marketing',
                element: <EmailMarketingLayout />,
                children: [
                  { index: true, element: <EmailMarketingIndexRedirect /> },
                  { path: 'campanhas', element: <EmailCampaignsPage /> },
                  { path: 'campanhas/nova', element: <EmailCampaignWizardPage /> },
                  { path: 'campanhas/:campaignId/editar', element: <EmailCampaignWizardPage /> },
                  { path: 'campanhas/:campaignId', element: <EmailCampaignDetailPage /> },
                  { path: 'templates', element: <EmailTemplatesPage /> },
                  { path: 'templates/novo', element: <EmailTemplateFormPage /> },
                  { path: 'templates/:templateId/editar', element: <EmailTemplateFormPage /> },
                  { path: 'modelos', element: <EmailTemplatesLegacyRedirect /> },
                  { path: 'configuracao', element: <EmailSettingsPage /> },
                ],
              },
              {
                path: 'sms-marketing',
                element: <SmsMarketingLayout />,
                children: [
                  { index: true, element: <SmsMarketingIndexRedirect /> },
                  { path: 'campanhas', element: <SmsCampaignsPage /> },
                  { path: 'campanhas/nova', element: <SmsCampaignWizardPage /> },
                  { path: 'campanhas/:campaignId/editar', element: <SmsCampaignWizardPage /> },
                  { path: 'campanhas/:campaignId', element: <SmsCampaignDetailPage /> },
                  { path: 'configuracao', element: <SmsSettingsPage /> },
                ],
              },
              {
                path: 'push-marketing',
                element: <PushMarketingLayout />,
                children: [
                  { index: true, element: <PushMarketingIndexRedirect /> },
                  { path: 'campanhas', element: <PushCampaignsPage /> },
                  { path: 'campanhas/nova', element: <AutomatedCampaignFormPage /> },
                  { path: 'campanhas/:campaignId/editar', element: <AutomatedCampaignFormPage /> },
                  { path: 'campanhas/:campaignId/resultados', element: <CampaignResultsPage /> },
                  { path: 'configuracao', element: <PushSettingsPage /> },
                ],
              },
              {
                path: 'campanhas-whatsapp',
                element: <LegacyWhatsappRedirect />,
              },
              { path: 'automacoes', element: <LegacyPushRedirect /> },
              { path: 'produtos/resgates', element: <ProductsPage mode="redemption" /> },
              { path: 'produtos', element: <ProductsPage /> },
              { path: 'influencer', element: <InfluencerListPage /> },
              { path: 'banners', element: <BannersPage /> },
              { path: 'fab-buttons', element: <FabButtonsPage /> },
              { path: 'verso-cartao', element: <VersoCartaoPage /> },
              { path: 'metabase', element: <MetabaseConfigPage /> },
              { path: 'configuracoes', element: <StoreSettingsPage /> },
              { path: 'tiers', element: <TiersPage /> },
              { path: 'tiers/ajustar-cliente', element: <TierOverridePage /> },
              // Configuração do cliente mora em /clientes/:id. As URLs antigas e o
              // atalho da inspeção só resolvem o clientId e redirecionam.
              { path: 'configurar', element: <PassbookToClientHubRedirect /> },
              { path: 'permissoes', element: <PassbookToClientHubRedirect /> },
              { path: 'permissoes/:section', element: <PassbookToClientHubRedirect /> },
            ],
          },
        ],
      },
    ],
  },
  { path: '/', element: <RootRedirect /> },
  { path: '*', element: <NotFound /> },
]);
